import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextInput } from '@/common/components/Inputs';
import Checkbox from '@/common/components/Inputs/Checkbox';
import styles from '@/common/containers/Forms/SetPassword/styles.less';
import { getMarketingSiteUrl } from '@/app/config';
import { useLocale } from '@/common/hooks';

const SetupAccountForm = ({
  email = '',
  firstName = '',
  lastName = '',
  onSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const legalDisclaimerUrl = getMarketingSiteUrl('/legal-disclaimer', locale);
  const [showPasswordTips, setShowPasswordTips] = useState(false);
  const passwordReq = t(
    'Password must be at least 8 characters, contain uppercase and lowercase, numbers and special symbols'
  );

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      yup.object({
        firstName: yup.string().trim().required(t('Enter first name')),
        lastName: yup.string().trim().required(t('Enter last name')),
        password: yup
          .string()
          .test('password', passwordReq, (value) => {
            if (!value) return false;
            if (value.length < 8) return false;
            if (!/[A-Z]+/.test(value)) return false;
            if (!/[a-z]+/.test(value)) return false;
            if (!/[0-9]+/.test(value)) return false;
            if (!/[^A-Za-z0-9]+/.test(value)) return false;
            return true;
          })
          .required(t('This field is required')),
        repeatPassword: yup
          .string()
          .required(t('Please repeat password'))
          .test('repeatPassword', t('Passwords do not match.'), function (value) {
            return value === this.parent.password;
          }),
        policy: yup
          .boolean()
          .oneOf(
            [true],
            t('You must accept the Privacy Policy and Terms of Service to continue.')
          ),
        legalDisclaimer: yup
          .boolean()
          .oneOf([true], t('You must accept the Legal Disclaimer to continue.')),
      })
    ),
    shouldUnregister: true,
    defaultValues: {
      email,
      firstName,
      lastName,
      password: '',
      repeatPassword: '',
      policy: false,
      legalDisclaimer: false,
    },
  });

  const { isDirty, isValid } = methods.formState;
  const repeatPasswordValue = methods.watch('repeatPassword');
  const passwordValue = methods.watch('password');

  useEffect(() => {
    methods.reset((currentValues) => ({
      ...currentValues,
      email,
      firstName,
      lastName,
    }));
  }, [email, firstName, lastName, methods]);

  const handleSubmit = methods.handleSubmit(async (form) => {
    await onSubmit(form);
  });

  return (
    <FormProvider {...methods}>
      <Box maxWidth={440} component="form" onSubmit={handleSubmit}>
        <input
          type="text"
          name="setup-username"
          autoComplete="username"
          value={email}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
            height: 0,
          }}
        />
        <TextInput
          name="email"
          label={t('Email')}
          required
          disabled
          autoComplete="off"
          sx={{ mb: 4 }}
        />
        <TextInput
          name="firstName"
          label={t('Your first name')}
          autoComplete="given-name"
          required
        />
        <TextInput
          name="lastName"
          label={t('Your last name')}
          autoComplete="family-name"
          sx={{ mt: 4 }}
          required
        />
        <TextInput
          name="password"
          label={t('Create password')}
          type="password"
          autoComplete="new-password"
          tip={showPasswordTips && passwordValue ? passwordReq : ''}
          inputProps={{
            maxLength: 50,
          }}
          sx={{ mt: 4 }}
          onFocus={() => setShowPasswordTips(true)}
          required
        />
        <TextInput
          name="repeatPassword"
          label={t('Confirm password')}
          type="password"
          autoComplete="new-password"
          tip={
            showPasswordTips && repeatPasswordValue
              ? t('Both passwords must match')
              : ''
          }
          inputProps={{
            maxLength: 50,
          }}
          sx={{ mt: 4 }}
          onFocus={() => setShowPasswordTips(true)}
          required
        />
        <Checkbox
          name="policy"
          sx={{ mt: 6 }}
          inputProps={{
            size: 'small',
            sx: { p: '0', pr: 2 },
          }}
          label={
            <span className={styles.policy}>
              {t('I accept the')}{' '}
              <a
                href="https://eac-public-files.s3.ca-central-1.amazonaws.com/EAC_Privacy_Policy.pdf"
                target="_blank"
                rel="noreferrer"
              >
                {t('Privacy Policy')}
              </a>{' '}
              {t('and')}{' '}
              <a
                href="https://eac-public-files.s3.ca-central-1.amazonaws.com/EAC_Terms_and_Conditions.pdf"
                target="_blank"
                rel="noreferrer"
              >
                {t('Terms of Service')}
              </a>
            </span>
          }
        />
        <Checkbox
          name="legalDisclaimer"
          sx={{ mt: 4 }}
          inputProps={{
            size: 'small',
            sx: {
              alignSelf: 'baseline',
              mt: '2px',
              p: '0',
              pr: 2,
            },
          }}
          label={
            <span style={{ whiteSpace: 'normal' }}>
              {t(
                'I understand that the information provided by this product is for general informational purposes only and is not legal advice. See'
              )}{' '}
              <a href={legalDisclaimerUrl} target="_blank" rel="noreferrer">
                {t('Legal Disclaimer')}
              </a>
            </span>
          }
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={!isDirty || !isValid || isPending}
          loading={isPending}
          sx={{ mt: 6 }}
        >
          {t('Continue')}
        </Button>
      </Box>
    </FormProvider>
  );
};

export default SetupAccountForm;
